
import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Plus, CreditCard } from 'lucide-react';
import { FairClickSite } from '@/types/fairclick';

const creditsFormSchema = z.object({
  credits: z.coerce.number().int().min(1, { message: 'Vous devez ajouter au moins 1 crédit' })
});

type CreditsFormValues = z.infer<typeof creditsFormSchema>;

interface AddCreditsFormProps {
  site: FairClickSite;
  userCredits: number;
  onAddCredits: (siteId: string, credits: number) => Promise<boolean>;
}

export const AddCreditsForm = ({ site, userCredits, onAddCredits }: AddCreditsFormProps) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<CreditsFormValues>({
    resolver: zodResolver(creditsFormSchema),
    defaultValues: {
      credits: 1
    }
  });
  
  const onSubmit = async (data: CreditsFormValues) => {
    setIsSubmitting(true);
    
    try {
      const success = await onAddCredits(site.id, data.credits);
      
      if (success) {
        form.reset();
        setOpen(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de crédits:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1">
          <Plus size={14} />
          <span>Ajouter des crédits</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard size={20} />
            Ajouter des crédits
          </DialogTitle>
          <DialogDescription>
            Site : {site.title}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="credits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de crédits à ajouter</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min={1} 
                      max={userCredits} 
                      {...field}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value)) {
                          field.onChange(1);
                        } else if (value > userCredits) {
                          field.onChange(userCredits);
                        } else if (value < 1) {
                          field.onChange(1);
                        } else {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Vous disposez de {userCredits} crédits
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter className="gap-2 sm:gap-0">
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuler</Button>
              </DialogClose>
              <Button type="submit" disabled={isSubmitting || userCredits === 0}>
                {isSubmitting ? "Ajout en cours..." : "Ajouter ces crédits"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
